import { DirectUpload } from "@rails/activestorage"
import { postJson } from "./http"
import { canvasResize, BLUR_PLACEHOLDER } from "./image_resize"
import { isObjectionable } from "./nsfw"

// Upload thumbnails for matched photos, in small chunks.
//
// Images go DIRECTLY to S3 via Active Storage direct upload — the bytes never pass
// through the Rails web dyno. Previously the web dyno base64-decoded and re-uploaded
// every image, and a big multi-device backfill would OOM the 512MB dyno. Now the web
// dyno only receives a tiny JSON payload ({client_ref, signed_id, blur}) and attaches
// the already-uploaded blob by id. No memory pressure, and it's faster (no double hop).
//
// fetchOne(clientRef) must resolve to a base64 data URI string, or null/undefined to
// skip that photo. The per-photo fetch differs per caller (bridge vs canvas), so it's
// injected; the upload is the shared part.

// data URI ("data:image/jpeg;base64,...") -> File, for DirectUpload.
function dataUriToFile(dataUri, filename) {
  const comma = dataUri.indexOf(",")
  const header = dataUri.slice(0, comma)
  const b64 = dataUri.slice(comma + 1).replace(/\s/g, "")
  const mime = (header.match(/data:([^;]+);/) || [])[1] || "image/jpeg"
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], filename, { type: mime })
}

// Send one image straight to S3 and resolve to the blob's signed_id (the server
// attaches by id; the bytes never touch the web dyno). Exported so the single-photo
// display-path uploader (photo_fetch) can reuse it.
//
// The phone→S3 step can fail transiently and never touches the web dyno, so a failure
// is otherwise invisible server-side. We retry a couple of times with backoff, and on
// final failure POST a report so it shows up in the server logs.
function accountGuard() {
  const account = () => document.querySelector('meta[name="photo-updates-account"]')?.content || null
  const expected = account()
  return () => account() === expected
}

function requireCurrentAccount(shouldContinue) {
  if (!shouldContinue()) throw new Error("Photo upload cancelled because the account changed.")
}

export async function directUploadImage(dataUri, clientRef, { retries = 2, shouldContinue = accountGuard() } = {}) {
  const safeName = clientRef.replace(/[^A-Za-z0-9]/g, "_")
  const file = dataUriToFile(dataUri, `${safeName}.jpg`)

  let lastError
  for (let attempt = 0; attempt <= retries; attempt++) {
    requireCurrentAccount(shouldContinue)
    try {
      // Each attempt is a fresh DirectUpload (new presigned URL + new PUT).
      return await new Promise((resolve, reject) => {
        new DirectUpload(file, "/rails/active_storage/direct_uploads", {
          directUploadWillStoreFileWithXHR(xhr) { if (!shouldContinue()) xhr.abort() }
        })
          .create((error, blob) => (error ? reject(error) : resolve(blob.signed_id)))
      })
    } catch (e) {
      lastError = e
      if (attempt < retries) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
    }
  }

  reportUploadFailure(clientRef, lastError)
  throw lastError
}

// Best-effort: tell the server an image upload failed after all retries, so it's
// visible in the logs (the phone→S3 hop never reaches the server on its own).
function reportUploadFailure(clientRef, error) {
  postJson("/library_photos/upload_failed", {
    client_ref: clientRef,
    error: String(error?.message || error || "unknown").slice(0, 300)
  }).catch(() => {})
}

// fetchBlurSource(clientRef) (optional) resolves to a base64 data URI from iOS's
// FAST image path, used only to make the tiny blurred preview. When provided, we run
// a blur-first pass so every match gets a blurred preview quickly and reliably (the
// blur is a small base64 column — no S3 — and the fast fetch doesn't stall on iCloud),
// then a best-effort high-quality pass for the clear thumbnail shown after a reveal.
export async function uploadThumbnailsInChunks(clientRefs, fetchOne, { chunkSize = 20, fetchBlurSource, shouldContinue = accountGuard() } = {}) {
  if (!clientRefs || clientRefs.length === 0) return

  const flushTo = async (batch) => {
    if (batch.length === 0) return []
    requireCurrentAccount(shouldContinue)
    await postJson("/library_photos/thumbnails", { thumbnails: batch })
    return []
  }

  // ── Pass 1: blurred previews for EVERY match, first and fast ──────────────
  // Done before the slow high-quality pass so the other person sees a blur (never
  // the off-white "not revealed" lock) even if pass 2 doesn't finish.
  if (fetchBlurSource) {
    let blurBatch = []
    for (const ref of clientRefs) {
      requireCurrentAccount(shouldContinue)
      let src = null
      try { src = await fetchBlurSource(ref) } catch (e) {}
      if (!src) continue
      const blur = await canvasResize(src, BLUR_PLACEHOLDER)
      if (!blur) continue
      blurBatch.push({ client_ref: ref, blur })
      if (blurBatch.length >= chunkSize) blurBatch = await flushTo(blurBatch)
    }
    await flushTo(blurBatch)
  }

  // ── Pass 2: high-quality clear thumbnails (shown after reveal) ────────────
  // Best-effort — needs a full-quality fetch (slower, can fail on iCloud), but the
  // blur from pass 1 already covers the before-reveal state.
  let batch = []
  for (const ref of clientRefs) {
    requireCurrentAccount(shouldContinue)
    const data = await fetchOne(ref)
    if (!data) continue
    requireCurrentAccount(shouldContinue)

    // Objectionable-content filter: flag explicit photos so the server drops their
    // matches and excludes them from future matching; don't upload their image.
    if (await isObjectionable(data)) {
      try { requireCurrentAccount(shouldContinue); await postJson("/library_photos/flag_explicit", { client_ref: ref }) } catch (e) {}
      continue
    }

    // Re-derive the blur from the high-quality image as a fallback (covers the case
    // where pass 1 was skipped or its fast fetch failed for this photo).
    const blur = await canvasResize(data, BLUR_PLACEHOLDER)
    try {
      const signedId = await directUploadImage(data, ref, { shouldContinue })
      batch.push({ client_ref: ref, signed_id: signedId, blur })
    } catch (e) {
      console.warn("[thumbnail-backfill] direct upload failed for", ref, e)
    }

    if (batch.length >= chunkSize) batch = await flushTo(batch)
  }
  await flushTo(batch)
}
